/**
 * Slice 18f — Batch "Suggest template" orchestration.
 *
 * Fully client-side: loads un-templated posts via `listAdsSocialPosts`
 * (`untemplated_only=true`), runs the existing per-post
 * `/api/ads-assistant/ingest-templateize` once per selected row with light
 * pacing between calls, lets the trainer accept/skip each proposal in a
 * review step, then PATCHes each accepted row through
 * `patchAdsSocialPost(id, { template_id, slot_fill_json })`.
 *
 * No new server endpoint is required — by design, this slice reuses the
 * single-post contract and adds a UI that orchestrates it.
 */

import { useCallback, useMemo, useState } from "react";
import {
  listAdsSocialPosts,
  patchAdsSocialPost,
  type AdsAssistantSocialPostRow,
} from "@/lib/adsAssistantClient";
import { postAdsIngestTemplateize } from "@/lib/adsAssistantClientEngagement";
import {
  ADS_BATCH_TEMPLATEIZE_FETCH_MAX,
  ADS_BATCH_TEMPLATEIZE_PACING_MS,
  ADS_BATCH_TEMPLATEIZE_SELECTION_MAX,
  buildApplyPatch,
  extractAdCopyForBatch,
  summarizeBatchResults,
  type AdsBatchTemplateizeResult,
} from "@/lib/adsBatchTemplateizeCore";

export type AdsBatchTemplateizePhase = "select" | "running" | "review" | "applying" | "done";

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function useAdsBatchTemplateize(opts: { onApplied?: () => void } = {}) {
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState<AdsBatchTemplateizePhase>("select");
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [posts, setPosts] = useState<AdsAssistantSocialPostRow[]>([]);
  const [totalUntemplated, setTotalUntemplated] = useState<number | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [results, setResults] = useState<Map<string, AdsBatchTemplateizeResult>>(new Map());
  const [generalError, setGeneralError] = useState<string | null>(null);

  const reset = useCallback(() => {
    setPhase("select");
    setPosts([]);
    setTotalUntemplated(null);
    setSelectedIds(new Set());
    setResults(new Map());
    setLoadError(null);
    setGeneralError(null);
  }, []);

  const openDialog = useCallback(async () => {
    reset();
    setOpen(true);
    setLoadingPosts(true);
    const r = await listAdsSocialPosts({
      untemplated_only: true,
      limit: ADS_BATCH_TEMPLATEIZE_FETCH_MAX,
      offset: 0,
    });
    setLoadingPosts(false);
    if (!r.ok) {
      setLoadError(r.error);
      return;
    }
    setPosts(r.data.posts ?? []);
    setTotalUntemplated(typeof r.data.total === "number" ? r.data.total : (r.data.posts ?? []).length);
  }, [reset]);

  const closeDialog = useCallback(() => {
    setOpen(false);
  }, []);

  const toggleSelected = useCallback((postId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(postId)) {
        next.delete(postId);
        return next;
      }
      if (next.size >= ADS_BATCH_TEMPLATEIZE_SELECTION_MAX) return prev;
      next.add(postId);
      return next;
    });
  }, []);

  const selectFirstN = useCallback(
    (n: number) => {
      const cap = Math.min(n, ADS_BATCH_TEMPLATEIZE_SELECTION_MAX);
      const next = new Set<string>();
      for (const p of posts.slice(0, cap)) next.add(p.id);
      setSelectedIds(next);
    },
    [posts],
  );

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const runSuggestions = useCallback(async () => {
    if (!selectedIds.size) return;
    setGeneralError(null);
    setPhase("running");

    const initial = new Map<string, AdsBatchTemplateizeResult>();
    for (const id of selectedIds) initial.set(id, { post_id: id, status: "pending" });
    setResults(initial);

    const selectedPosts = posts.filter((p) => selectedIds.has(p.id));
    for (const post of selectedPosts) {
      setResults((prev) => {
        const next = new Map(prev);
        next.set(post.id, { post_id: post.id, status: "running" });
        return next;
      });

      const inputs = extractAdCopyForBatch(post);
      if (!inputs) {
        setResults((prev) => {
          const next = new Map(prev);
          next.set(post.id, {
            post_id: post.id,
            status: "skipped",
            reason: "No ad copy or creative description on this row.",
          });
          return next;
        });
        continue;
      }

      const r = await postAdsIngestTemplateize(inputs);
      if (!r.ok) {
        setResults((prev) => {
          const next = new Map(prev);
          next.set(post.id, { post_id: post.id, status: "error", error: r.error });
          return next;
        });
      } else {
        const { template_id, template_name, slot_fills, notes } = r.data;
        setResults((prev) => {
          const next = new Map(prev);
          next.set(post.id, {
            post_id: post.id,
            status: "proposed",
            template_id,
            template_name,
            slot_fills,
            notes,
            accepted: !!template_id,
          });
          return next;
        });
      }

      await sleep(ADS_BATCH_TEMPLATEIZE_PACING_MS);
    }

    setPhase("review");
  }, [posts, selectedIds]);

  const toggleAccept = useCallback((postId: string) => {
    setResults((prev) => {
      const r = prev.get(postId);
      if (!r || r.status !== "proposed" || !r.template_id) return prev;
      const next = new Map(prev);
      next.set(postId, { ...r, accepted: !r.accepted });
      return next;
    });
  }, []);

  const acceptAllWithTemplate = useCallback(() => {
    setResults((prev) => {
      const next = new Map(prev);
      for (const [id, r] of prev) {
        if (r.status === "proposed" && r.template_id) {
          next.set(id, { ...r, accepted: true });
        }
      }
      return next;
    });
  }, []);

  const acceptNone = useCallback(() => {
    setResults((prev) => {
      const next = new Map(prev);
      for (const [id, r] of prev) {
        if (r.status === "proposed") next.set(id, { ...r, accepted: false });
      }
      return next;
    });
  }, []);

  const applyAccepted = useCallback(async () => {
    setPhase("applying");
    setGeneralError(null);
    const snapshot = Array.from(results.values());
    for (const r of snapshot) {
      const patch = buildApplyPatch(r);
      if (!patch) continue;
      const resp = await patchAdsSocialPost(r.post_id, patch);
      setResults((prev) => {
        const next = new Map(prev);
        if (!resp.ok) {
          next.set(r.post_id, { post_id: r.post_id, status: "error", error: resp.error });
        } else if (r.status === "proposed") {
          next.set(r.post_id, {
            post_id: r.post_id,
            status: "applied",
            template_id: patch.template_id,
            template_name: r.template_name,
          });
        }
        return next;
      });
    }
    setPhase("done");
    opts.onApplied?.();
  }, [results, opts]);

  const summary = useMemo(() => summarizeBatchResults(Array.from(results.values())), [results]);

  return {
    open,
    openDialog,
    closeDialog,
    phase,
    loadingPosts,
    loadError,
    posts,
    totalUntemplated,
    selectedIds,
    toggleSelected,
    selectFirstN,
    clearSelection,
    results,
    summary,
    runSuggestions,
    toggleAccept,
    acceptAllWithTemplate,
    acceptNone,
    applyAccepted,
    generalError,
  };
}
